import { useCallback, useEffect, useState } from 'react';
import { useAsync } from '@nxsys/hooks/useAsync';
import { getDashboardRequiredAlertsList } from '@nxsys/services/api/dashboard';
import {
  GetDashboardRequiredAlertsListParams,
  GetDashboardRequiredAlertsListResponse,
  RequiredAlertsListType,
} from '@nxsys/store/slices/dashboard/types';
import { showMessage } from '@nxsys/utils/message';
import { getErrorMessage } from '@nxsys/utils/getErrorMessage';

type UseGetDashboardRequiredAlertsListParams = {
  type: RequiredAlertsListType;
  // NX-4682: forwarded to every list request so the popup honours the same
  // payment-model scope as the dashboard card (card == popup count).
  paymentModels?: string[];
};

type UseGetDashboardRequiredAlertsListReturn = {
  loading: boolean;
  error: Error | null;
  data: GetDashboardRequiredAlertsListResponse | null;
  fetchRequiredAlertsList: (
    params: Omit<GetDashboardRequiredAlertsListParams, 'type'>,
  ) => Promise<void>;
  queryParams: GetDashboardRequiredAlertsListParams;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
};

export const useGetDashboardRequiredAlertsList = ({
  type,
  paymentModels,
}: UseGetDashboardRequiredAlertsListParams): UseGetDashboardRequiredAlertsListReturn => {
  const {
    loading,
    error,
    value: data,
    run,
  } = useAsync<
    GetDashboardRequiredAlertsListResponse,
    GetDashboardRequiredAlertsListParams
  >(getDashboardRequiredAlertsList);

  const [searchTerm, setSearchTerm] = useState('');
  const [queryParams, setQueryParams] =
    useState<GetDashboardRequiredAlertsListParams>({
      type,
      page: 0,
      size: 20,
    });

  useEffect(() => {
    if (error) {
      showMessage({
        type: 'error',
        content: getErrorMessage(error),
      });
    }
  }, [error]);

  const fetchRequiredAlertsList = useCallback(
    async (params: Omit<GetDashboardRequiredAlertsListParams, 'type'>) => {
      // NX-4682: always attach the card's payment-model scope so the popup
      // count matches the card. `undefined` is omitted by axios (no filter),
      // mirroring the card COUNT call.
      const fullParams = { ...params, type, paymentModels };
      setQueryParams(fullParams);
      await run(fullParams);
    },
    [type, paymentModels, run],
  );

  return {
    loading,
    error,
    data,
    fetchRequiredAlertsList,
    queryParams,
    searchTerm,
    setSearchTerm,
  };
};
